import { IWorkorder } from '@cbgms/api'
import { useNotifier } from '@cbgms/base/notifier'
import WorkorderSelectionModal from 'app/workorder/components/WorkorderSelectionModal'
import { createWorkorder, loadWorkordersByVehicle } from 'app/workorder/state/workorder/actions'
import useGetWorkordersForSelection from 'contexts/WorkorderContext/use-get-workorders-for-selection'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

export interface WorkorderSelectionDialogProps {
  open: boolean
  onSelect: (selectedWoUUID: string) => void
  onClose: () => void
  vehicleUUID?: string
  accountUUID?: string
}

const useCreateNewWorkorderWithSideEffects = ({ vehicleUUID, accountUUID, onSelect, onClose }: WorkorderSelectionDialogProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const notifier = useNotifier()
  // Loading vehicle workorders actually doesn't belong here,
  // If we don't dispatch this, the workorders on the vehicle dashboard will get stale
  const loadVehicleWorkorders = useCallback(async () => {
    dispatch(
      loadWorkordersByVehicle({
        fields: 'Workorder.*',
        page: 1,
        perPage: 200,
        search: `Workorder.VehicleUUID|=|${vehicleUUID}`,
        orderBy: 'Workorder.CreatedAt|DESC'
      })
    )
  }, [dispatch, vehicleUUID])

  return useCallback(async () => {
    try {
      const data = await dispatch(createWorkorder({ AccountUUID: accountUUID || undefined, VehicleUUID: vehicleUUID || undefined }))
      if (vehicleUUID) loadVehicleWorkorders()
      onSelect(data.Data.UUID)
    } catch {
      notifier.error(t('global.something_went_wrong'))
    } finally {
      onClose()
    }
  }, [dispatch, vehicleUUID, accountUUID, loadVehicleWorkorders, notifier, onClose, t])
}

export const WorkorderSelectionDialog = (props: WorkorderSelectionDialogProps) => {
  const { open, onClose, onSelect, vehicleUUID } = props
  const { data, isLoading } = useGetWorkordersForSelection(open, vehicleUUID)
  const createNewWorkorderWithSideEffects = useCreateNewWorkorderWithSideEffects(props)
  return (
    <WorkorderSelectionModal
      modalOpen={open}
      isLoading={isLoading}
      closeModal={onClose}
      workorders={data || []}
      onClickSelectWorkorder={({ UUID }: IWorkorder) => onSelect(UUID)}
      onClickCreateNewWorkorder={createNewWorkorderWithSideEffects}
    />
  )
}
